import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { injectTestId } from '@cvent/nucleus-test-automation';
import NotificationAlert from '@components/notifications/NotificationAlert';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@hooks/useTheme';
import { useTranslate } from 'nucleus-text';
import useTimezone from '@hooks/useTimezone';
import TextLink from '@cvent/carina/components/TextLink';
import Link from 'next/link';
import Button from '@cvent/carina/components/Button';
import { VideoIcon } from '@components/shared/V1ToV2Icon';
import lineClamp from '@tools/lineClamp';
import appointmentToastImage from '@images/appointment-toast-image.svg';
import sessionToastImage from '@images/session-toast-image.svg';
import { ENGAGEMENT_PANEL_IDS } from '@components/engagement/constants';
import { useRouter } from 'next/router';
import useSessionStatus from '@tools/sessionUtils/useSessionStatus';
import { SessionStatus } from '@tools/sessionUtils/SessionStatus';
import { useAnalytics } from '@hooks/useAnalytics';
import useAuthorization from '@hooks/useAuthorization';

const panelId = ENGAGEMENT_PANEL_IDS.AGENDA_ITEM_REMINDERS;

const useStyles = () => {
  const { font } = useTheme();
  const { isMobile } = useBreakpoints();

  return {
    linkedContent: {
      marginTop: 8,
      span: {
        color: font.color.interactive.base,
        fontSize: font.base.size.s,
        paddingLeft: 0,
        fontWeight: font.base.weight.bold,
        '&:hover': {
          textDecoration: 'underline'
        }
      }
    },
    multiItemHeading: {
      fontWeight: font.base.weight.normal,
      marginBottom: 5,
      paddingRight: isMobile ? 55 : 40
    },
    multiItemLink: {
      fontSize: isMobile ? font.base.size.xxs : font.base.size['4']
    },
    itemTime: {
      fontSize: isMobile ? font.base.size.ssx : font.base.size.s,
      color: font.color.soft,
      lineHeight: '17.5px',
      marginBottom: 2
    },
    itemTitle: {
      fontSize: isMobile ? font.base.size.s : font.base.size.l,
      lineHeight: isMobile ? '18px' : font.lineHeight.heading,
      marginBottom: 2
    },
    itemParticipants: {
      fontSize: isMobile ? font.base.size.ssx : font.base.size.s,
      lineHeight: isMobile ? '20px' : font.lineHeight.heading,
      color: font.color.base,
      fontWeight: font.base.weight.regular,
      marginBottom: isMobile ? 10 : 15
    }
  };
};

const AgendaItemReminderToast = ({
  toastRef,
  topPosition,
  bottomPosition,
  messages = [],
  eventId,
  dismissTimeout,
  onDismiss
}) => {
  const { isMobile } = useBreakpoints();
  const styles = useStyles();
  const { translate } = useTranslate();
  const analytics = useAnalytics();
  const { date } = useTimezone();
  const router = useRouter();
  const { isAttendee } = useAuthorization(true);
  const {
    end,
    id,
    name,
    participants,
    speakers,
    start,
    type,
    virtualLink,
    virtual
  } = messages[0];

  let itemToastImage;
  let headingText;
  let joinButtonText;
  let isAppointment = false;
  let hostsOrSpeakers;
  let isVirtual = false;
  if (messages.length === 1) {
    switch (type) {
      case 'SESSION': {
        itemToastImage = sessionToastImage;
        hostsOrSpeakers = speakers || [];
        isVirtual = virtual;
        headingText = translate('Notifications-Single-Session-Toast-Heading');
        joinButtonText = !isAttendee(id)
          ? translate('Notifications-Single-Session-Card-Join-HighPermission')
          : translate('Notifications-Single-Session-Card-Join');
        break;
      }
      case 'APPOINTMENT': {
        isAppointment = true;
        itemToastImage = appointmentToastImage;
        hostsOrSpeakers = participants || [];
        isVirtual = virtual || virtualLink;
        headingText = translate(
          'Notifications-Single-Appointment-Toast-Heading'
        );
        joinButtonText = translate(
          'Notifications-Single-Appointment-Card-Join'
        );
        break;
      }
      default: {
        break;
      }
    }
  }

  useEffect(() => {
    analytics.track({
      id: 'seamless_transition',
      type: 'toast',
      action: 'open'
    });
    // We only want to call this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status = useSessionStatus({ start, end });

  const showDirectAppointmentLink =
    isAppointment &&
    virtualLink &&
    (status === SessionStatus.DURING ||
      status === SessionStatus.LESS_THAN_FIVE_MIN_BEFORE);

  const startingTime = date(new Date(start), {
    hour: 'numeric',
    minute: 'numeric'
  });
  const endingTime = date(new Date(end), {
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short'
  });

  return (
    <div
      {...injectTestId('item-toast-container')}
      css={{
        position: 'fixed',
        zIndex: 1600,
        right: 8,
        padding: 20,
        ...(isMobile ? { bottom: bottomPosition } : { top: topPosition })
      }}
    >
      <NotificationAlert
        eventId={eventId}
        toastRef={toastRef}
        testID={panelId}
        id={panelId}
        displayPosition={
          isMobile
            ? {
                bottom: bottomPosition,
                right: 15,
                left: 15
              }
            : {
                position: 'fixed',
                right: 8,
                top: topPosition
              }
        }
        cursor={'default'}
        notificationsData={[
          {
            body: '',
            id: panelId,
            icon: !isMobile ? itemToastImage : null
          }
        ]}
        iconAriaLabel
        autoHideTimeout={dismissTimeout}
        headingText={!isMobile ? headingText : ''}
        linkedContentStyles={styles.linkedContent}
        onDismiss={() => {
          analytics.track({
            id: 'seamless_transition',
            type: 'toast',
            action: 'dismiss'
          });
          onDismiss();
        }}
      >
        {messages.length === 1 ? (
          <div css={styles.itemContainer}>
            <div css={styles.itemTime}>
              {startingTime} — {endingTime}
            </div>

            <div {...injectTestId('agenda-item-reminder-title')}>
              <Link
                href={
                  isAppointment
                    ? `/events/${eventId}/schedule?apptId=${id}`
                    : `/events/${eventId}/sessions/${id}`
                }
                passHref
                legacyBehavior
              >
                <TextLink
                  css={[styles.itemTitle, lineClamp(2)]}
                  onClick={() => {
                    analytics.track({
                      id: 'seamless_transition',
                      type: 'toast',
                      action: 'title-click'
                    });
                    onDismiss();
                  }}
                  testID="agenda-item-reminder-title-link"
                >
                  {name}
                </TextLink>
              </Link>
            </div>
            {hostsOrSpeakers && (
              <div
                css={[styles.itemParticipants, lineClamp(2)]}
                {...injectTestId('agenda-item-reminder-participants')}
              >
                {hostsOrSpeakers
                  .map(hostOrSpeaker =>
                    translate('Universal-FullName', hostOrSpeaker)
                  )
                  .join(translate('Universal-List-Delimiter'))}
              </div>
            )}
            {isVirtual && (
              <Button
                appearance="filled"
                size={isMobile ? 'm' : 'l'}
                testID={`item-toast-join-button-${id}`}
                text={joinButtonText}
                icon={VideoIcon}
                onClick={() => {
                  analytics.track({
                    id: 'seamless_transition',
                    type: 'toast',
                    action: 'join-click'
                  });
                  if (isAppointment) {
                    if (showDirectAppointmentLink) {
                      window.open(`${virtualLink}&source=event`, '_self');
                    } else {
                      router.push(`/events/${eventId}/schedule?apptId=${id}`);
                    }
                  } else {
                    router.push(
                      `/events/${eventId}/sessions/${id}?autoPlay=true`
                    );
                  }
                  onDismiss();
                }}
              />
            )}
          </div>
        ) : (
          <div css={{ paddingLeft: 8 }}>
            <div css={styles.multiItemHeading}>
              {translate('Notifications-Multiple-Items-Toast-Heading')}
            </div>
            <Link href={`/events/${eventId}/schedule`} passHref legacyBehavior>
              <TextLink
                css={styles.multiItemLink}
                onClick={() => {
                  analytics.track({
                    id: 'seamless_transition',
                    type: 'toast',
                    action: 'my-schedule'
                  });
                  onDismiss();
                }}
                testID="agenda-item-reminder-my-schedule-link"
              >
                {translate('Notifications-Multiple-Items-Toast-Link')}
              </TextLink>
            </Link>
          </div>
        )}
      </NotificationAlert>
    </div>
  );
};

AgendaItemReminderToast.propTypes = {
  toastRef: PropTypes.any,
  eventId: PropTypes.string,
  dismissTimeout: PropTypes.number,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      end: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      participants: PropTypes.array,
      speakers: PropTypes.array,
      start: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['SESSION', 'APPOINTMENT']),
      virtualLink: PropTypes.string,
      virtual: PropTypes.bool
    })
  ),
  topPosition: PropTypes.number.isRequired,
  bottomPosition: PropTypes.number.isRequired,
  onDismiss: PropTypes.func.isRequired
};

export default AgendaItemReminderToast;
